import { api } from './index';
import { MEMBERS_TABLE_TAG } from './membersTableTag';

import type { AxiosBaseQueryError } from './index';
import type {
  CompanyMemberStatus,
  MembersTableApiRow,
  MembersTableCoachCountFilter,
  MembersTableFacets,
  MembersTableResponse,
  MembersTableSortDirection,
} from './userApi';
import type {
  B2BProvisioningStatus,
  Company,
  CompanyFeatureFlags,
  CompanyStats,
  SeatType,
  SessionCount,
  User,
} from '@/types';
import type { SerializedError } from '@reduxjs/toolkit';

export interface CreateCompanyRequest {
  domains: string[];
  name: string;
}

export interface UpdateCompanyDetailsRequest {
  adminEmails?: string[];
  avatarUrl?: string;
  contractedSeats?: number | null;
  culture?: string;
  disallowedEmails?: string[];
  domainMembersSelfPay?: boolean;
  domains?: string[];
  emails?: string[];
  featureFlags?: CompanyFeatureFlags;
  goals?: string[];
  industry?: string;
  linkedin?: string;
  location?: string;
  name?: string;
  pocEmail?: string;
  pocName?: string;
  requiredAuthMethod?: 'oauth' | 'magic_link' | null;
  sessionTimeoutLength?: number | null;
  vision?: string;
  website?: string;
}

export interface UpdateCompanyStateRequest {
  state: 'active' | 'inactive';
}

export type CompanyMutationError = AxiosBaseQueryError | SerializedError;

type CompanyMutationErrorData = {
  b2bProvisioningStatus?: B2BProvisioningStatus;
  code?: string;
  domains?: string[];
  error?: string;
  [key: string]: unknown;
};

const isSafeObject = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

const isAxiosBaseQueryError = (error: unknown): error is AxiosBaseQueryError =>
  isSafeObject(error) &&
  'data' in error &&
  (error.status === undefined || typeof error.status === 'number');

const getCompanyMutationErrorData = (
  error: unknown,
): CompanyMutationErrorData | undefined => {
  if (!isAxiosBaseQueryError(error)) return undefined;
  if (isSafeObject(error.data)) return error.data;
  return undefined;
};

export const getCompanyMutationFieldErrors = (
  error: unknown,
): Record<string, string[]> => {
  const data = getCompanyMutationErrorData(error);
  if (!isSafeObject(data)) return {};

  return Object.fromEntries(
    Object.entries(data).filter(
      (entry): entry is [string, string[]] =>
        Array.isArray(entry[1]) &&
        entry[1].length > 0 &&
        entry[1].every((message) => typeof message === 'string'),
    ),
  );
};

export const getCompanyMutationErrorMessage = (error: unknown): string => {
  const data = getCompanyMutationErrorData(error);
  return typeof data?.error === 'string' ? data.error : 'Something went wrong';
};

export interface B2BOrgCandidate {
  organizationId: string;
  organizationName: string;
  organizationSlug?: string;
}

interface LinkCompanyB2BOrgResponse {
  company: Company;
  linkedOrganization?: B2BOrgCandidate;
  linked_organization?: B2BOrgCandidate;
}

interface CompaniesResponse {
  items: Company[];
  pages: number;
  total: number;
}

export type CompanyMember = User & {
  cohortTags: { id: string; name: string }[];
};

export interface CompanyMembersResponse {
  facets?: MembersTableFacets;
  items: CompanyMember[];
  pages: number;
  total: number;
}

export interface CompanyMembersFilters {
  chemCall?: boolean;
  coachesAssigned?: MembersTableCoachCountFilter[];
  coachIds?: string[];
  cohortTagIds?: string[];
  endDateStatuses?: string[];
  insightsStatuses?: string[];
  memberStatuses?: string[];
  noUpcomingSessions?: boolean;
  scheduledSessions?: boolean;
  seatTypes?: SeatType[];
}

const coachRowToUser = (coach: MembersTableApiRow['coaches'][number]): User => {
  const [firstName = coach.name, ...lastNameParts] = coach.name.split(' ');

  return {
    avatarUrl: coach.avatarUrl ?? undefined,
    firstName,
    fullName: coach.name,
    id: coach.id,
    lastName: lastNameParts.join(' '),
  } as User;
};

const memberTableRowToUser = (row: MembersTableApiRow): CompanyMember =>
  ({
    ...row.user,
    adminNote: row.adminNote ?? undefined,
    coaches: row.coaches.map(coachRowToUser),
    cohortTags: row.cohortTags ?? [],
    contractEnd: row.contractEnd,
    createdAt: row.joinedAt,
    growthPlans: [
      {
        id: '',
        northStar: '',
        oneYearVision: '',
        started: !!row.growthPlanStarted,
      },
    ],
    insightsStatus: row.insightsStatus,
    memberStatus: row.memberStatus ?? undefined,
  }) as CompanyMember;

const compactRequest = <T extends object>(request: T): T =>
  Object.fromEntries(
    Object.entries(request).filter(([, value]) => value !== undefined),
  ) as T;

const toUpdateCompanyRequest = (
  data: UpdateCompanyDetailsRequest | UpdateCompanyStateRequest,
): UpdateCompanyDetailsRequest | UpdateCompanyStateRequest => {
  if ('state' in data) return { state: data.state };

  return compactRequest({
    adminEmails: data.adminEmails,
    avatarUrl: data.avatarUrl,
    contractedSeats: data.contractedSeats,
    culture: data.culture,
    disallowedEmails: data.disallowedEmails,
    domainMembersSelfPay: data.domainMembersSelfPay,
    domains: data.domains,
    emails: data.emails,
    featureFlags: data.featureFlags,
    goals: data.goals,
    industry: data.industry,
    linkedin: data.linkedin,
    location: data.location,
    name: data.name,
    pocEmail: data.pocEmail,
    pocName: data.pocName,
    requiredAuthMethod: data.requiredAuthMethod,
    sessionTimeoutLength: data.sessionTimeoutLength,
    vision: data.vision,
    website: data.website,
  });
};

export const companyApi = api.injectEndpoints({
  endpoints: (builder) => ({
    createCompany: builder.mutation<Company, CreateCompanyRequest>({
      invalidatesTags: ['Company'],
      query: (request) => ({
        data: {
          company: { domains: request.domains, name: request.name },
        },
        method: 'POST',
        url: '/companies',
      }),
    }),
    deleteCompany: builder.mutation<void, string>({
      invalidatesTags: (_result, _error, id) => [
        { id, type: 'Company' },
        'Company',
      ],
      query: (id) => ({
        method: 'DELETE',
        url: `/companies/${id}`,
      }),
    }),
    getCompanies: builder.query<
      CompaniesResponse,
      { page?: number; search?: string }
    >({
      providesTags: ['Company'],
      query: ({ page = 0, search = '' }) => ({
        method: 'GET',
        url: `/companies?search=${search}&page=${page}`,
      }),
    }),
    getCompany: builder.query<Company, string>({
      providesTags: (_result, _error, id) => [{ id, type: 'Company' }],
      query: (id) => ({ method: 'GET', url: `/companies/${id}` }),
    }),
    getCompanyMembers: builder.query<
      CompanyMembersResponse,
      {
        companyId: string;
        companyMemberStatus: CompanyMemberStatus;
        filters?: CompanyMembersFilters;
        page?: number;
        limit?: number;
        search?: string;
        sortBy?: string;
        sortDirection?: MembersTableSortDirection;
      }
    >({
      // MEMBERS_TABLE_TAG makes cohort tag changes refetch this list.
      providesTags: ['User', MEMBERS_TABLE_TAG],
      query: ({
        companyId,
        companyMemberStatus,
        filters,
        page = 1,
        limit = 20,
        search,
        sortBy,
        sortDirection,
      }) => ({
        method: 'GET',
        params: {
          ...filters,
          companyIds: [companyId],
          companyMemberStatus,
          limit,
          page,
          search: search || undefined,
          sortBy,
          sortDirection,
          view: 'members_table',
        },
        url: '/users',
      }),
      transformResponse: (response: MembersTableResponse) => ({
        facets: response.facets,
        items: response.items.map(memberTableRowToUser),
        pages: response.pages,
        total: response.total,
      }),
    }),
    getCompanySessions: builder.query<Record<string, SessionCount>, string>({
      providesTags: ['Session'],
      query: (id) => ({ method: 'GET', url: `/companies/${id}/sessions` }),
    }),
    getCompanyStats: builder.query<CompanyStats, string>({
      providesTags: (_result, _error, id) => [{ id, type: 'Company' }],
      query: (companyId) => ({
        method: 'GET',
        url: `/companies/${companyId}/stats`,
      }),
    }),
    updateCompany: builder.mutation<
      Company,
      {
        data: UpdateCompanyDetailsRequest | UpdateCompanyStateRequest;
        id: string;
      }
    >({
      invalidatesTags: (_result, _error, { id }) => [
        { id, type: 'Company' },
        'Company',
      ],
      query: ({ data, id }) => ({
        data: { company: toUpdateCompanyRequest(data) },
        method: 'PATCH',
        url: `/companies/${id}`,
      }),
    }),
    getCompanyB2BOrgCandidates: builder.mutation<
      { candidates: B2BOrgCandidate[] },
      { companyId: string; domains?: string[]; name?: string }
    >({
      query: ({ companyId, domains, name }) => ({
        method: 'GET',
        params: {
          ...(domains?.length ? { domains } : {}),
          ...(name ? { name } : {}),
        },
        url: `/companies/${companyId}/b2b_org_candidates`,
      }),
    }),
    linkCompanyB2BOrg: builder.mutation<
      LinkCompanyB2BOrgResponse,
      {
        companyId: string;
        create_if_missing?: boolean;
        domains?: string[];
        name?: string;
        organization_id?: string;
      }
    >({
      invalidatesTags: (_result, _error, { companyId }) => [
        { id: companyId, type: 'Company' },
        'Company',
      ],
      query: ({ companyId, ...body }) => ({
        data: body,
        method: 'POST',
        url: `/companies/${companyId}/link_b2b_org`,
      }),
    }),
  }),
});

export const {
  useCreateCompanyMutation,
  useDeleteCompanyMutation,
  useGetCompaniesQuery,
  useGetCompanyMembersQuery,
  useGetCompanyQuery,
  useGetCompanyB2BOrgCandidatesMutation,
  useGetCompanySessionsQuery,
  useGetCompanyStatsQuery,
  useLinkCompanyB2BOrgMutation,
  useUpdateCompanyMutation,
} = companyApi;
